/**
 * SettingsPageFrame — Account and Location settings as a page, not a dialog.
 *
 * The same header as the account's or location's dashboard (useSettingsHeader):
 * breadcrumbs, the account or location as its own switcher, and Dashboard |
 * Settings with Settings active. Below it a grouped, searchable list of
 * sections on the left, the open section on the right, and a save bar pinned
 * to the bottom. On a phone the section list is a screen of its own:
 * choosing a section opens it, and "‹ All settings" goes back to the list.
 *
 * The ways out are the breadcrumbs, the Dashboard tab, the switcher and the
 * main navigation; each leaves through the unsaved-changes question. (The
 * separate Back and Close buttons are gone.)
 *
 * It renders into the page host beside the app's main content
 * (#settings-page-host, see App.tsx), which hides the view underneath while a
 * settings page is up and shows only the most recent one when two stack (a
 * location opened from its account's Locations list). Without a host (tests,
 * or a host-less shell) it covers the window instead.
 *
 * Widths: on a computer (992px and up) the list is a sidebar; on a tablet a
 * section picker above the content opens it as a sheet; on a phone it is the
 * first screen. Tab rows inside keep their labels at every width.
 *
 * The save bar appears only while something is unsaved. It works on the open
 * section: "Save changes" saves its edits and "Discard" throws them away,
 * while edits in other sections wait (they keep their dot in the list and are
 * named). Leaving the page is the host's business, and asks about every
 * unsaved section.
 *
 * Design system: the page ground `bg`, the section title in `sectionTitle`,
 * the tablet picker and sheet on `surface` with a `border`, text from the
 * shared text roles. Tab rows inside are the segmented tabs (TabsKeepLabels
 * draws them, see packages/ui theme/tabs.ts).
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { QueryClientContext } from '@tanstack/react-query';
import { BackButton, CloseButton, TabsKeepLabels, sectionSpec, textRole, tokenVars } from '@turn-connect/ui';
import { useResponsive } from '@turn-connect/hooks';
import { SectionNav } from './SectionNav';
import { SettingsSaveBar as SaveBarView, joinAreas } from './SettingsSaveBar';
import { SavesImmediatelyCaption } from '../SavesImmediatelyCaption';
import { InstantCaptionShownContext } from '../instantCaption';
import { PlaceHeader } from '../../navigation/place/PlaceHeader';
import { usePlacePageStyle } from '../../navigation/place/usePlacePageStyle';
import { SettingsPlaceHeader, type SettingsHeaderInput } from './useSettingsHeader';
import { DensityProvider } from '../../ui/ds';

export { joinAreas };

export const SETTINGS_PAGE_HOST_ID = 'settings-page-host';

/**
 * Hides the app view while a settings page is in the host, and shows only the
 * most recent page when two stack. Rendered once by the app shell.
 */
// !important: each page sets display inline (flex), which would otherwise
// beat these rules and leave an older page showing above the newest one (a
// location opened from its account's Locations list appeared below the fold,
// so Open looked like it did nothing).
export const SETTINGS_PAGE_HOST_CSS = `
.tc-main-area:has(#${'settings-page-host'} > *) > .tc-main-view { display: none !important; }
#${'settings-page-host'} > *:not(:last-child) { display: none !important; }
`;

export interface SettingsSectionItem {
  id: string;
  label: string;
  /** e.g. an "N custom" count. */
  badge?: React.ReactNode;
  /**
   * How the section saves (the registry's save mode): an 'instant' section
   * gets the "Saves automatically" caption under its title, the same way on
   * every page.
   */
  save?: 'explicit' | 'instant' | 'mixed' | 'checked' | 'none';
  /** The section's tabs, so search can find them. */
  tabs?: ReadonlyArray<{ id: string; label: string; keywords?: readonly string[] }>;
  /** Other words people search with. */
  keywords?: readonly string[];
  /** A one-line status for the phone list ("3 locations"). */
  status?: string;
}

export interface SettingsSectionGroup {
  label: string;
  items: SettingsSectionItem[];
}

export interface SettingsSaveBar {
  /**
   * Ids of the sections holding unsaved edits, in list order. Read after every
   * edit inside the page (input, change, click, key), because the sections
   * report their state through save handles rather than re-rendering the page.
   */
  getDirtySections: () => string[];
  canSave: boolean;
  saving: boolean;
  /** Save the open section's edits (and only those). */
  onSave: (sectionId: string) => void;
  /** Throw away the open section's edits (and only those). */
  onDiscard: (sectionId: string) => void;
}

export interface SettingsPageFrameProps {
  /** Page heading when there is no header, e.g. "Northgate settings"; also the page's accessible name. */
  title: string;
  /** What the shared page header shows (breadcrumbs, switchable title, Dashboard | Settings). */
  header?: SettingsHeaderInput | null;
  /** Crumbs above the heading when there is no header. */
  breadcrumb?: React.ReactNode;
  groups: SettingsSectionGroup[];
  activeSection: string;
  /** A section was chosen; `tabId` when a search result named one of its tabs. */
  onSectionChange: (id: string, tabId?: string) => void;
  /** Leave the page for the view it was opened from (the Dashboard tab when there is no header). */
  onBack: () => void;
  /** Hidden for someone who can save nothing here. */
  saveBar?: SettingsSaveBar | null;
  /** Error and view-only notices, shown above the section. */
  notice?: React.ReactNode;
  /** On a phone, start on the section list rather than the section (no section was asked for). */
  startOnList?: boolean;
  testId?: string;
  children: React.ReactNode;
}

const SettingsPageFrame: React.FC<SettingsPageFrameProps> = ({
  title,
  header,
  breadcrumb,
  groups,
  activeSection,
  onSectionChange,
  onBack,
  saveBar,
  notice,
  startOnList = false,
  testId = 'settings-page',
  children,
}) => {
  const { isMobile, isTablet } = useResponsive();
  const pageColumn = usePlacePageStyle();
  // The header reads the person's accounts; a host without data access (a
  // test, a bare shell) gets the plain title and the Dashboard tab.
  const hasData = !!useContext(QueryClientContext);
  const [phonePane, setPhonePane] = useState<'list' | 'section'>(startOnList ? 'list' : 'section');
  const [sheetOpen, setSheetOpen] = useState(false);
  const pickerRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  // The page fills the window below the app header, so the save bar sits at
  // the bottom of the screen even when a section is short.
  const frameRef = useRef<HTMLElement>(null);
  const [frameTop, setFrameTop] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = frameRef.current;
      if (el) setFrameTop(Math.max(0, el.getBoundingClientRect().top + window.scrollY));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const host = typeof document !== 'undefined' ? document.getElementById(SETTINGS_PAGE_HOST_ID) : null;
  // A page opening (or stacking on another) starts at the top of the screen,
  // not wherever the page underneath was scrolled to.
  useEffect(() => {
    if (host) window.scrollTo?.({ top: 0 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Which sections hold unsaved edits, re-read after each edit in the page.
  const [dirtyIds, setDirtyIds] = useState<string[]>([]);
  const getDirty = saveBar?.getDirtySections;
  const recheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recheckDirty = React.useCallback(() => {
    if (recheckTimer.current) clearTimeout(recheckTimer.current);
    recheckTimer.current = setTimeout(() => {
      const next = getDirty ? getDirty() : [];
      setDirtyIds((prev) => (prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next));
    }, 0);
  }, [getDirty]);
  useEffect(() => {
    recheckDirty();
    return () => {
      if (recheckTimer.current) clearTimeout(recheckTimer.current);
    };
  });

  // Closing the browser tab or reloading with unsaved edits: the browser's own warning.
  const hasUnsaved = dirtyIds.length > 0;
  useEffect(() => {
    if (!hasUnsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  // A new section starts at its top.
  useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 });
  }, [activeSection]);

  // The tablet sheet closes on Escape and hands focus back to the picker.
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const closeSheet = () => {
    setSheetOpen(false);
    pickerRef.current?.focus();
  };

  const allItems = groups.flatMap((g) => g.items);
  const labelOf = (id: string) => allItems.find((i) => i.id === id)?.label ?? id;
  const activeLabel = labelOf(activeSection);
  const activeGroup = groups.find((g) => g.items.some((i) => i.id === activeSection))?.label ?? '';
  const activeInstant = allItems.find((i) => i.id === activeSection)?.save === 'instant';

  const choose = (id: string, tabId?: string) => {
    onSectionChange(id, tabId);
    if (isMobile) setPhonePane('section');
    if (sheetOpen) setSheetOpen(false);
  };

  const showRail = !isMobile && !isTablet;
  const showList = isMobile && phonePane === 'list';
  const showMain = !isMobile || phonePane === 'section';
  // A `checked` section saves through its own review, so the bar doesn't
  // offer Save changes there; from other sections it still points to it.
  const activeDirty = dirtyIds.includes(activeSection) && allItems.find((i) => i.id === activeSection)?.save !== 'checked';
  const others = dirtyIds.filter((id) => id !== activeSection).map((id) => ({ id, label: labelOf(id) }));
  const nav = (touch: boolean, autoFocusSearch = false) => (
    <SectionNav
      groups={groups}
      activeSection={activeSection}
      dirtyIds={dirtyIds}
      onChoose={choose}
      touch={touch}
      autoFocusSearch={autoFocusSearch}
    />
  );

  const crumbs = breadcrumb ? (
    <nav
      aria-label="Breadcrumb"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        ...textRole('meta'),
        ...(isMobile ? { marginTop: 4 } : {}),
      }}
    >
      {breadcrumb}
    </nav>
  ) : null;

  const frame = (
    <section
      ref={frameRef}
      aria-label={title}
      data-testid={testId}
      onInputCapture={recheckDirty}
      onChangeCapture={recheckDirty}
      onClickCapture={recheckDirty}
      onKeyUpCapture={recheckDirty}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: host ? `calc(100dvh - ${frameTop}px)` : '100%',
        background: tokenVars.bg,
        color: tokenVars.text.primary,
        position: host ? 'relative' : 'fixed',
        ...(host ? {} : { inset: 0, zIndex: 900, overflow: 'auto' }),
      }}
    >
      <div
        style={{
          // The page column every place page shares: the header here starts
          // where the dashboards' does, and the body below follows it.
          ...pageColumn,
          paddingTop: isMobile ? 14 : 24,
        }}
      >
        {header && hasData ? (
          <SettingsPlaceHeader input={header} compact={isMobile} onBack={onBack} />
        ) : (
          <>
            {crumbs}
            <PlaceHeader
              crumbs={[]}
              title={title}
              compact={isMobile}
              tabs={{ active: 'settings', dashboard: { onOpen: onBack }, settings: { onOpen: () => undefined } }}
              testId="settings-page-header"
            />
          </>
        )}
        {/* With the shared header, the host's breadcrumb slot only carries
            helpers that render nothing (the phone list's status loader). */}
        {header && breadcrumb}
      </div>

      <div
        // The same column as the header above, so the section list sits
        // under the breadcrumbs and the section under the title.
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: showRail ? '252px minmax(0, 1fr)' : 'minmax(0, 1fr)',
          minHeight: 0,
          ...pageColumn,
        }}
      >
        {showRail && (
          <div
            style={{
              // Lined up with the header's left edge; no panel of its own.
              padding: '20px 12px 16px 0',
              position: 'sticky',
              top: 0,
              alignSelf: 'start',
              maxHeight: '100%',
              overflowY: 'auto',
              minWidth: 0,
            }}
          >
            {nav(false)}
          </div>
        )}
        {showList && <div style={{ padding: '12px 0', background: tokenVars.bg }}>{nav(true)}</div>}
        {showMain && (
          <div
            ref={mainRef}
            data-testid="settings-section-main"
            style={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              padding: showRail ? '20px 0 24px 24px' : isMobile ? '8px 0 16px' : '20px 0 24px',
            }}
          >
            {isMobile && (
              <BackButton
                label="All settings"
                onClick={() => setPhonePane('list')}
                touch
                testId="settings-all-sections"
                style={{ alignSelf: 'flex-start', marginBottom: 8 }}
              />
            )}
            {isTablet && (
              <button
                ref={pickerRef}
                type="button"
                onClick={() => setSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={sheetOpen}
                data-testid="settings-section-picker"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  width: '100%',
                  marginBottom: 16,
                  padding: '9px 12px',
                  minHeight: 48,
                  ...textRole('value'),
                  background: tokenVars.surface,
                  border: `1px solid ${tokenVars.border}`,
                  borderRadius: sectionSpec.radius,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span>
                  <span style={{ ...textRole('label'), display: 'block' }}>{activeGroup || 'Settings'}</span>
                  {activeLabel}
                  {activeDirty && <span style={{ color: tokenVars.textAccent }}> ●</span>}
                </span>
                <span aria-hidden style={{ color: tokenVars.text.muted }}>
                  ▾
                </span>
              </button>
            )}
            <h2 style={{ ...textRole('sectionTitle'), margin: '0 0 12px' }} data-testid="settings-section-title">
              {activeLabel}
            </h2>
            {/* One caption, the same everywhere, for sections that save as you go;
                the panels inside then leave out their own. */}
            {activeInstant && <SavesImmediatelyCaption testId="settings-section-saves" style={{ margin: '-4px 0 12px' }} />}
            {notice}
            <InstantCaptionShownContext.Provider value={activeInstant}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TabsKeepLabels>{children}</TabsKeepLabels>
              </div>
            </InstantCaptionShownContext.Provider>
          </div>
        )}
      </div>

      {saveBar && (
        <SaveBarView
          activeLabel={activeLabel}
          activeDirty={activeDirty}
          others={others}
          saving={saveBar.saving}
          canSave={saveBar.canSave}
          onSave={() => saveBar.onSave(activeSection)}
          onDiscard={() => saveBar.onDiscard(activeSection)}
          onGoTo={(id) => choose(id)}
          touch={isMobile}
        />
      )}

      {sheetOpen && isTablet && (
        <div
          data-testid="settings-section-sheet"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSheet();
          }}
          style={{
            position: host ? 'absolute' : 'fixed',
            inset: 0,
            zIndex: 5,
            background: tokenVars.overlay,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '72px 24px 24px',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Settings sections"
            // Keep keyboard focus inside the sheet while it is open.
            onKeyDown={(e) => {
              if (e.key !== 'Tab') return;
              const items = Array.from(
                e.currentTarget.querySelectorAll<HTMLElement>(
                  'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
                )
              );
              if (items.length === 0) return;
              const first = items[0];
              const last = items[items.length - 1];
              if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
              } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
              }
            }}
            style={{
              width: '100%',
              maxWidth: 520,
              maxHeight: '100%',
              overflowY: 'auto',
              background: tokenVars.surface,
              border: `1px solid ${tokenVars.border}`,
              borderRadius: sectionSpec.radius,
              padding: '10px 10px 14px',
              boxShadow: `0 20px 60px ${tokenVars.overlay}`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 4px 10px' }}>
              <b style={textRole('cardTitle')}>Settings</b>
              <CloseButton onClick={closeSheet} touch testId="settings-section-sheet-close" />
            </div>
            {nav(false, true)}
          </div>
        </div>
      )}
    </section>
  );

  // Settings are a form screen: default density (40px buttons, 44px tabs), even when
  // opened from a compact page (the Accounts dashboard), whose provider reaches here.
  return ReactDOM.createPortal(<DensityProvider density="default">{frame}</DensityProvider>, host ?? document.body);
};

export default SettingsPageFrame;
